import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightIcon,
  Avatar,
  Button,
  Card,
  CarIcon,
  ChatIcon,
  ChevronLeftIcon,
  ChoiceRow,
  Heading,
  LeafIcon,
  MessageIcon,
  MusicIcon,
  NoSmokingIcon,
  PawIcon,
  Price,
  RouteTimeline,
  SchematicMap,
  SelectChip,
  VerifiedBadge,
  VerifiedBadgeIcon,
  WahineIcon,
  cn,
} from '@openroad/ui';
import {
  api,
  formatCo2,
  formatDay,
  formatDuration,
  formatMoney,
  formatTime,
  unwrap,
} from '../lib/api';
import { useSession } from '../lib/session';
import { useIsMobileLayout } from '../lib/viewport';

/** How the export words each verification badge. */
const BADGE_LABEL: Record<string, string> = {
  ID: 'ID verified',
  PHONE: 'Phone verified',
  EMAIL: 'Email verified',
  PHOTO: 'Photo verified',
};

const CHATTINESS_LABEL = {
  QUIET: 'Quiet ride',
  BALANCED: 'Chat if you feel like it',
  CHATTY: 'Happy to chat',
} as const;

/**
 * What went wrong, in words a passenger can act on.
 *
 * The API's own message is the useful one — it names the actual rule, like
 * "This ride is women-only" or "Only 2 seats left" — so it is shown rather than
 * replaced with something vaguer.
 */
function bookingErrorMessage(error: unknown): string {
  const message = (error as { message?: string } | null)?.message;
  return message && message.length > 0 ? message : 'That booking did not go through. Try again.';
}

/** Seat counts a passenger may pick, capped by what is left and by the API's max. */
const MAX_SEATS_PER_BOOKING = 4;

export function TripScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const mobile = useIsMobileLayout();

  const trip = useQuery({
    queryKey: ['trip', id],
    queryFn: () => unwrap(api.GET('/trips/{id}', { params: { path: { id } } })),
    enabled: id !== '',
  });

  const [seats, setSeats] = useState(1);
  const [pickupPointId, setPickupPointId] = useState<string | null>(null);

  const session = useSession();
  const queryClient = useQueryClient();

  /**
   * The price, from the server, for the seats currently chosen.
   *
   * Not computed here. Prices are fee-inclusive (ADR-0009), so seats x seat
   * price happens to give the same total today — but the moment that stops
   * being true, a number the browser worked out for itself is a number that
   * disagrees with the charge.
   */
  const quote = useQuery({
    queryKey: ['trip-quote', id, seats],
    queryFn: () =>
      unwrap(api.GET('/trips/{id}/quote', { params: { path: { id }, query: { seats } } })),
    enabled: id !== '',
  });

  const book = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/bookings', {
          body: { tripId: id, seats, ...(pickupPointId ? { pickupPointId } : {}) },
        }),
      ),
    onSuccess: async (booking) => {
      // The ride now has fewer seats, and the member has one more trip.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['trip', id] }),
        queryClient.invalidateQueries({ queryKey: ['trips', 'mine'] }),
      ]);
      navigate(`/bookings/${booking.id}/confirmed`);
    },
  });

  const openThread = useMutation({
    mutationFn: () => unwrap(api.POST('/threads', { body: { tripId: id } })),
    onSuccess: (result) => navigate(`/messages/${result.thread.id}`),
  });

  const ride = trip.data;

  // The first pickup point is selected once the ride arrives, matching the
  // export's filled first radio. Keyed on the trip so navigating between two
  // rides does not carry a point that belongs to the other one.
  useEffect(() => {
    setSeats(1);
    setPickupPointId(ride?.pickupPoints[0]?.id ?? null);
  }, [ride?.id, ride?.pickupPoints]);

  const seatChoices = useMemo(() => {
    if (!ride) return [1];
    const most = Math.min(ride.seatsAvailable, MAX_SEATS_PER_BOOKING);
    return Array.from({ length: Math.max(most, 1) }, (_, index) => index + 1);
  }, [ride]);

  if (trip.isPending) {
    return (
      <main className="mx-auto max-w-[1080px] px-6 py-14">
        <p className="text-body text-muted">Loading this ride…</p>
      </main>
    );
  }

  if (trip.isError || !ride) {
    return (
      <main className="mx-auto max-w-[560px] px-6 py-14">
        <Card radius="panel" className="p-7 text-center">
          <Heading level="panel" className="mb-2">
            We could not find that ride
          </Heading>
          <p className="m-0 mb-6 text-base text-muted">
            It may have been cancelled, or the link may be out of date.
          </p>
          <Button variant="cta" onClick={() => navigate('/search')}>
            Back to search
          </Button>
        </Card>
      </main>
    );
  }

  const style = [
    { icon: <ChatIcon size={19} strokeWidth={1.7} />, label: CHATTINESS_LABEL[ride.chattiness] },
    {
      icon: <MusicIcon size={19} strokeWidth={1.7} />,
      label: ride.musicWelcome ? 'Music welcome' : 'Prefers no music',
    },
    {
      icon: <PawIcon size={19} strokeWidth={1.7} />,
      label: ride.petsAllowed ? 'Pets welcome' : 'No pets',
    },
    {
      icon: <NoSmokingIcon size={19} strokeWidth={1.7} />,
      label: ride.smokingAllowed ? 'Smoking allowed' : 'No smoking',
    },
  ];

  // Only a fallback while the quote is in flight, so the card never flashes an
  // empty total. The server's figure replaces it as soon as it arrives.
  const seatTotal = {
    cents: ride.pricePerSeat.cents * seats,
    currency: ride.pricePerSeat.currency,
  };
  const signedIn = session.member !== null;
  const total = formatMoney(quote.data?.price.total ?? seatTotal);

  /**
   * The single booking action, wherever it is drawn.
   *
   * On a wide screen it is the bottom of the 360px card, which is in view
   * because the card is sticky. A phone has no room for that card beside
   * anything, so it goes back into the flow — and a booking button that has
   * scrolled a screen and a half away is a booking button nobody presses. The
   * button moves to a bar pinned above the tab bar instead.
   *
   * It is built once and placed once. Rendering it in both and hiding one would
   * put two "Book this seat" buttons in the accessibility tree.
   */
  const bookingAction = signedIn ? (
    <Button
      variant="cta"
      size="hero"
      block
      onClick={() => book.mutate()}
      disabled={book.isPending || ride.seatsAvailable < seats}
    >
      {book.isPending ? 'Booking…' : ride.instantBook ? 'Book this seat' : 'Request this seat'}
    </Button>
  ) : (
    <Button
      variant="cta"
      size="hero"
      block
      onClick={() => navigate('/sign-in', { state: { from: `/trips/${ride.id}` } })}
    >
      Sign in to book
    </Button>
  );

  const bookingNote = book.isError ? (
    <p className="mt-2 mb-0 text-center text-mini text-danger" role="alert">
      {bookingErrorMessage(book.error)}
    </p>
  ) : !ride.instantBook ? (
    <p className="mt-2 mb-0 text-center text-mini text-subtle">
      {ride.driver.displayName.split(' ')[0]} approves each request.
    </p>
  ) : null;

  return (
    <main
      className={cn(
        'mx-auto max-w-[1080px] px-6 pt-5 pb-14',
        // Room to scroll clear of the sticky booking bar. Generous rather than
        // exact: extra space at the end of a scroll is invisible, and a page
        // whose last paragraph sits under a fixed bar is not.
        mobile && 'pb-[7.5rem]',
      )}
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 flex cursor-pointer items-center gap-[7px] text-label font-semibold text-muted"
      >
        <ChevronLeftIcon size={16} /> Back to results
      </button>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_360px]">
        {/* --------------------------------------------------------- left */}
        <div className="flex flex-col gap-[18px]">
          <Card radius="panel" flush>
            <div className="px-[22px] pt-5 pb-4">
              <div className="mb-1 flex items-center gap-3">
                <span className="font-display text-heading font-bold tracking-display">
                  {ride.origin.name}
                </span>
                <ArrowRightIcon size={20} strokeWidth={2.2} className="text-pohutukawa" />
                <span className="font-display text-heading font-bold tracking-display">
                  {ride.destination.name}
                </span>
              </div>
              <div className="text-label text-muted">
                {formatDay(ride.departureAt)} · {formatDuration(ride.durationSeconds)} drive ·{' '}
                {formatCo2(ride.co2SavedGramsPerSeat)} CO₂ saved per seat
              </div>
            </div>

            <div className="relative h-[170px] border-t border-line-soft">
              <SchematicMap height={170} />
            </div>

            <div className="px-[22px] py-[18px]">
              <RouteTimeline
                departure={{
                  time: formatTime(ride.departureAt),
                  place: ride.origin.name,
                  note: 'Pickup at your selected point',
                }}
                arrival={{
                  time: formatTime(ride.arrivalAt),
                  place: ride.destination.name,
                  note: 'Drop-off in the city centre',
                }}
              />
              {ride.stops.length > 0 ? (
                <p className="mt-4 mb-0 text-meta text-subtle">
                  Stopping at {ride.stops.map((stop) => stop.name).join(', ')}
                </p>
              ) : null}
            </div>
          </Card>

          {/* driver */}
          <Card radius="panel" className="p-[22px]">
            <div className="flex items-start gap-4">
              <Avatar
                initials={ride.driver.initials}
                colour={ride.driver.avatarColour}
                size={60}
                name={ride.driver.displayName}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-display text-title font-bold">
                    {ride.driver.displayName}
                  </span>
                  {ride.driver.verifiedBadges.includes('ID') ? (
                    <VerifiedBadgeIcon size={17} />
                  ) : null}
                </div>
                <div className="mt-0.5 text-label text-muted">
                  <span className="font-bold text-ngahere">
                    ★ {ride.driver.ratingAverage ?? '—'}
                  </span>{' '}
                  · {ride.driver.reviewCount} {ride.driver.reviewCount === 1 ? 'review' : 'reviews'}{' '}
                  · Member since {new Date(ride.driver.memberSince).getFullYear()} ·{' '}
                  {ride.driver.responseRatePct}% response
                </div>
                {ride.driver.bio ? (
                  <p className="mt-3 mb-0 text-base leading-[1.55] text-ink-2">{ride.driver.bio}</p>
                ) : null}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {ride.driver.verifiedBadges.map((badge) => (
                <VerifiedBadge key={badge}>{BADGE_LABEL[badge] ?? badge}</VerifiedBadge>
              ))}
            </div>
          </Card>

          {/* vehicle */}
          <Card radius="panel" className="p-[22px]">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-hover">
                <CarIcon size={22} strokeWidth={1.7} className="text-ink-2" />
              </span>
              <div>
                <div className="text-title-sm font-bold">
                  {ride.vehicle.make} {ride.vehicle.model}
                </div>
                <div className="text-meta text-muted">
                  {ride.vehicle.year} · {ride.vehicle.colour}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-[9px] sm:grid-cols-2">
              {ride.vehicle.amenities.map((amenity) => (
                <div key={amenity} className="flex items-center gap-2 text-label text-ink-2">
                  <span className="h-[5px] w-[5px] rounded-full bg-ngahere" aria-hidden="true" />
                  {amenity}
                </div>
              ))}
            </div>
          </Card>

          {/* ride style */}
          <Card radius="panel" className="p-[22px]">
            <Heading level="card" as="h3" className="mb-4">
              Ride style
            </Heading>
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              {style.map((item) => (
                <div key={item.label} className="flex items-center gap-[11px]">
                  <span className="text-moana">{item.icon}</span>
                  <span className="text-base text-ink">{item.label}</span>
                </div>
              ))}
            </div>
            {ride.womenOnly ? (
              <div className="mt-[18px] flex items-center gap-[11px] border-t border-line-soft pt-4">
                <WahineIcon size={19} className="text-wahine" />
                <span className="text-base text-ink">Women passengers only</span>
              </div>
            ) : null}
            {ride.description ? (
              <p className="mt-[18px] mb-0 border-t border-line-soft pt-4 text-label leading-[1.55] text-ink-2">
                {ride.description}
              </p>
            ) : null}
          </Card>
        </div>

        {/* ------------------------------------------------- booking card */}
        <Card
          radius="panel"
          className="p-5 shadow-booking lg:sticky lg:top-[84px]"
          aria-labelledby="booking-heading"
        >
          <h2 id="booking-heading" className="sr-only">
            Book a seat
          </h2>

          <div className="mb-1 flex items-baseline justify-between">
            <Price amount={formatMoney(ride.pricePerSeat)} size="detail" />
            <span className="text-meta text-subtle">per seat</span>
          </div>
          <div className="mb-[18px] text-meta text-muted">
            {ride.seatsAvailable} {ride.seatsAvailable === 1 ? 'seat' : 'seats'} left
          </div>

          <span className="mb-2 block text-meta font-semibold">Seats</span>
          <div className="mb-[18px] flex gap-2">
            {seatChoices.map((count) => (
              <SelectChip key={count} selected={seats === count} onClick={() => setSeats(count)}>
                {count} {count === 1 ? 'seat' : 'seats'}
              </SelectChip>
            ))}
          </div>

          {ride.pickupPoints.length > 0 ? (
            <>
              <span className="mb-2 block text-meta font-semibold" id="pickup-label">
                Pickup point
              </span>
              <div
                role="radiogroup"
                aria-labelledby="pickup-label"
                className="mb-5 flex flex-col gap-2"
              >
                {ride.pickupPoints.map((point) => (
                  <ChoiceRow
                    key={point.id}
                    selected={pickupPointId === point.id}
                    onClick={() => setPickupPointId(point.id)}
                  >
                    {point.label}
                  </ChoiceRow>
                ))}
              </div>
            </>
          ) : null}

          <div className="mb-4 flex items-center justify-between border-y border-line-soft py-3.5">
            <span className="text-base font-semibold">Total</span>
            {/*
              Seat price times seats, exactly as the export's booking card shows
              it — and, because pricing is fee-inclusive, exactly what will be
              charged. There is no fee row because there is nothing added on
              top: the platform's cut comes out of this figure (ADR-0009).
            */}
            <span className="font-display text-heading-sm font-extrabold">{total}</span>
          </div>

          {!mobile ? (
            <>
              {bookingAction}
              {bookingNote}
            </>
          ) : null}

          <Button
            variant="outline"
            block
            className="mt-2.5 h-[46px]"
            leadingIcon={<MessageIcon size={17} strokeWidth={1.9} />}
            onClick={() =>
              signedIn
                ? openThread.mutate()
                : navigate('/sign-in', { state: { from: `/trips/${ride.id}` } })
            }
            disabled={openThread.isPending}
          >
            Message {ride.driver.displayName}
          </Button>

          <div className="mt-4 flex items-center gap-2 rounded-[10px] bg-ngahere-tint px-3 py-[11px]">
            <LeafIcon size={17} strokeWidth={1.8} className="text-leaf" />
            <span className="text-meta text-ngahere-ink">
              You&rsquo;ll save <strong>{formatCo2(ride.co2SavedGramsPerSeat)}</strong> of CO₂ on
              this trip
            </span>
          </div>
        </Card>
      </div>

      {/* The booking bar: the card's total and its CTA, pinned directly above
          the tab bar so both are one thumb away from anywhere on the page. */}
      {mobile ? (
        <div className="fixed inset-x-0 above-tab-bar z-30 border-t border-line bg-surface px-4 py-3 shadow-sheet">
          {bookingNote}
          <div className="mt-1 flex items-center gap-4">
            <div className="flex-none">
              <div className="text-meta text-subtle">Total</div>
              <div className="font-display text-heading-sm font-extrabold">{total}</div>
            </div>
            <div className="flex-1">{bookingAction}</div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
